import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { isG4 } from "./public_brand";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import FAQsPage from "./pages/FAQsPage";
import PrivacyPage from "./pages/PrivacyPage";
import FreeDesignTermsPage from "./pages/FreeDesignTermsPage";
import CookiesPage from "./pages/CookiesPage";
// WP6-B — Studio G4 public pages (host-based routing). These components
// are static-imported but only mounted when `isG4` is true.
import G4HomePage from "./pages/g4/HomePage";
import G4RequestQuote from "./pages/g4/RequestQuote";
import G4ProjectDetail from "./pages/g4/ProjectDetail";
import G4Privacy from "./pages/g4/Privacy";
import G4Cookies from "./pages/g4/Cookies";
import AdminLayout from "./components/admin/AdminLayout";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import MarketingWebsite from "./pages/admin/MarketingWebsite";
import EnquiriesList from "./pages/admin/EnquiriesList";
import ProjectsLanding from "./pages/admin/ProjectsLanding";
import Planner from "./pages/admin/Planner";
import ServicePackagesShell from "./pages/admin/ServicePackagesShell";
import TemplateEditorPage from "./pages/admin/TemplateEditorPage";
import AdminConfigPage, { WorkstageTaskTemplatePage } from "./pages/admin/AdminConfigPage";
import EnquiryNew from "./pages/admin/EnquiryNew";
import ProjectWorkspace from "./pages/admin/ProjectWorkspace";
import FeasibilityStudy from "./pages/admin/FeasibilityStudy";
import {
    PackageDetailPage,
    PackageActivationPage,
    CommercialHubPage,
    ContractDetailPage,
    ContractChangeDetailPage,
} from "./pages/admin/B9B10Pages";
import {
    PaymentCertificationPage,
    GlobalProgrammePage,
    MiniProgrammeWs6DetailPage,
    ConstructionDeliveryPage,
    ContractAdminPage,
    ReviewCompletionPage,
} from "./pages/admin/B11B12Pages";
import {
    PostCompletionPage,
    RequirementDetailPage,
    ClosureReadinessPage,
} from "./pages/admin/B13Pages";
import { ProjectFiles, FileDetail, ProjectCommunications, ProjectActivity } from "./pages/admin/B3aPages";
import { ProjectProgramme, ProgrammeItemDetail, ProjectMiniProgrammes, MiniProgrammeDetail, ProjectTasks } from "./pages/admin/B3bPages";
import { ProjectClientActions, ProjectAttention } from "./pages/admin/B3cPages";
import { WS1Page, RFIList } from "./pages/admin/B4Pages";
import { WS2Page } from "./pages/admin/B5Pages";
import { FeedbackReviewPage } from "./pages/admin/B7aPages";
import PublicRFIForm from "./pages/PublicRFIForm";
import PublicSignAgreement from "./pages/PublicSignAgreement";
import MetaEvents from "./pages/admin/MetaEvents";
import AdminToolsPage from "./pages/admin/AdminToolsPage";
import UsersPage from "./pages/admin/UsersPage";
import { ContentLibraryList, ContentLibraryDetail } from "./pages/admin/ContentLibraryPages";
import PortalRoutes from "./pages/portal/PortalRoutes";
import { AttributionProvider } from "./context/AttributionContext";
import { AuthProvider } from "./context/AuthContext";

// Legacy redirect — keeps historical email links in email_service.py:62 and
// any admin bookmark resolving after Phase 2 retirement of EnquiryDetail.jsx.
// See /app/memory/retire_enquiry_detail.md for context.
function EnquiryDetailRedirect() {
    const { id } = useParams();
    return <Navigate to={`/admin/projects/${id}`} replace />;
}

export default function App() {
    // WP6-B — dynamic-import the G4 CSS only on the G4 brand host so
    // LRS routes never evaluate G4 styles. CSS imports are
    // side-effectful; a dynamic import keeps bundling tree-shakable.
    useEffect(() => {
        if (isG4) {
            import("./styles/g4.css").catch(() => {});
        }
    }, []);
    return (
        <HelmetProvider>
        <AuthProvider>
            <AttributionProvider>
                <BrowserRouter>
                    <Routes>
                        {/* WP6-B — host-based public routes. On the Studio G4
                           brand (studio-g4.co.uk, or ?brand=g4 preview
                           override) we mount the G4 marketing site. On
                           every other host we leave the existing LRS
                           public Layout untouched. */}
                        {isG4 ? (
                            <>
                                <Route path="/" element={<G4HomePage />} />
                                <Route path="/projects/:projectId" element={<G4ProjectDetail />} />
                                <Route path="/request-quote" element={<G4RequestQuote />} />
                                <Route path="/privacy" element={<G4Privacy />} />
                                <Route path="/cookies" element={<G4Cookies />} />
                            </>
                        ) : (
                            <Route element={<Layout />}>
                                <Route path="/" element={<Home />} />
                                <Route path="/faqs" element={<FAQsPage />} />
                                <Route path="/privacy" element={<PrivacyPage />} />
                                <Route path="/free-design-terms" element={<FreeDesignTermsPage />} />
                                <Route path="/cookies" element={<CookiesPage />} />
                            </Route>
                        )}

                        {/* Admin (auth-guarded within AdminLayout) */}
                        <Route path="/rfi/:token" element={<PublicRFIForm />} />
                        <Route path="/sign/:token" element={<PublicSignAgreement />} />
                        {/* C-A — Client Portal.  Canonical /portal namespace
                           (C-A Addendum §2).  Audience-separated auth is
                           enforced server-side; frontend hides admin routes. */}
                        <Route path="/portal/*" element={<PortalRoutes />} />
                        <Route path="/admin/login" element={<AdminLogin />} />
                        <Route path="/admin" element={<AdminLayout />}>
                            <Route index element={<AdminDashboard />} />
                            <Route path="marketing-website" element={<MarketingWebsite />} />
                            <Route path="projects" element={<ProjectsLanding />} />
                            <Route path="planner" element={<Planner />} />
                            <Route path="projects/:id" element={<ProjectWorkspace />} />
                            <Route path="projects/:id/files" element={<ProjectFiles />} />
                            <Route path="projects/:id/files/:fileId" element={<FileDetail />} />
                            <Route path="projects/:id/communications" element={<ProjectCommunications />} />
                            <Route path="projects/:id/activity" element={<ProjectActivity />} />
                            <Route path="projects/:id/programme" element={<ProjectProgramme />} />
                            <Route path="projects/:id/feasibility-study" element={<FeasibilityStudy />} />
                            <Route path="projects/:id/programme/:itemId" element={<ProgrammeItemDetail />} />
                            <Route path="projects/:id/mini-programmes" element={<ProjectMiniProgrammes />} />
                            <Route path="projects/:id/mini-programmes/:mid" element={<MiniProgrammeDetail />} />
                            <Route path="projects/:id/tasks" element={<ProjectTasks /> } />
                            <Route path="projects/:id/client-actions" element={<ProjectClientActions />} />
                            <Route path="projects/:id/attention" element={<ProjectAttention />} />
                            <Route path="projects/:id/ws1" element={<WS1Page />} />
                            <Route path="projects/:id/ws2" element={<WS2Page />} />
                            <Route path="projects/:id/ws4/feedback-sets/:fsid" element={<FeedbackReviewPage />} />
                            <Route path="projects/:id/rfi" element={<RFIList />} />
                            <Route path="projects/:projectId/packages/:pkgId" element={<PackageDetailPage />} />
                            <Route path="projects/:projectId/packages/:pkgId/activation" element={<PackageActivationPage />} />
                            <Route path="projects/:projectId/commercial-hub" element={<CommercialHubPage />} />
                            <Route path="projects/:projectId/contracts/:contractId" element={<ContractDetailPage />} />
                            <Route path="projects/:projectId/contract-changes/:changeId" element={<ContractChangeDetailPage />} />
                            <Route path="projects/:projectId/ws6/payment-certification" element={<PaymentCertificationPage />} />
                            <Route path="projects/:projectId/ws6/programme" element={<GlobalProgrammePage />} />
                            <Route path="projects/:projectId/ws6/mini-programmes/:mid" element={<MiniProgrammeWs6DetailPage />} />
                            <Route path="projects/:projectId/ws6/construction/:pkgId" element={<ConstructionDeliveryPage />} />
                            <Route path="projects/:projectId/ws6/contract-admin/:pkgId" element={<ContractAdminPage />} />
                            <Route path="projects/:projectId/ws6/completion" element={<ReviewCompletionPage />} />
                            <Route path="projects/:projectId/ws7" element={<PostCompletionPage />} />
                            <Route path="projects/:projectId/ws7/requirements/:requirementId" element={<RequirementDetailPage />} />
                            <Route path="projects/:projectId/ws7/closure-readiness" element={<ClosureReadinessPage />} />
                            <Route path="service-packages" element={<ServicePackagesShell />} />
                            <Route path="service-packages/new" element={<TemplateEditorPage />} />
                            <Route path="service-packages/drafts/:draftId" element={<TemplateEditorPage />} />
                            <Route path="service-packages/:code/edit" element={<TemplateEditorPage />} />
                            <Route path="config" element={<AdminConfigPage />} />
                            <Route path="config/task-templates/ws1" element={<WorkstageTaskTemplatePage ws={1} />} />
                            <Route path="config/task-templates/ws2" element={<WorkstageTaskTemplatePage ws={2} />} />
                            <Route path="config/task-templates/ws3" element={<WorkstageTaskTemplatePage ws={3} />} />
                            <Route path="config/task-templates/ws4" element={<WorkstageTaskTemplatePage ws={4} />} />
                            <Route path="config/task-templates/ws5" element={<WorkstageTaskTemplatePage ws={5} />} />
                            <Route path="config/task-templates/ws6" element={<WorkstageTaskTemplatePage ws={6} />} />
                            <Route path="config/task-templates/ws7" element={<WorkstageTaskTemplatePage ws={7} />} />
                            <Route path="enquiries" element={<EnquiriesList />} />
                            <Route path="enquiries/new" element={<EnquiryNew />} />
                            <Route path="enquiries/:id" element={<EnquiryDetailRedirect />} />
                            <Route path="meta-events" element={<MetaEvents />} />
                            <Route path="tools" element={<AdminToolsPage />} />
                            <Route path="users" element={<UsersPage />} />
                            <Route path="content-library" element={<ContentLibraryList />} />
                            <Route path="content-library/:code" element={<ContentLibraryDetail />} />
                        </Route>

                        <Route path="*" element={isG4 ? <G4HomePage /> : <Home />} />
                    </Routes>
                </BrowserRouter>
            </AttributionProvider>
        </AuthProvider>
        </HelmetProvider>
    );
}
